import { useCallback, useMemo } from 'react';

import type { AlinePlateValue } from '@aline/common';
import { PlateQueries } from '@aline/common';
import type { Apollo } from 'src/gql/generated';
import {
  useCheckWorkflowAuthorization,
  useGetMentionableUsers,
  useGetStorablePermissions,
  useIsAuthorizedToEditStorable,
} from 'src/hooks';
import { useDeepCompareMemo } from 'use-deep-compare';

import { ALINE_AI_MENTION_VALUE } from './aline-ai-mention';
import type { MentionEntity } from './types';

export type MentionEntityOwner = Pick<Apollo.User, '__typename' | 'displayName' | 'email' | 'id'>;

interface MentionedUserSummary {
  displayName: string;
  email: string;
}

export function useMentionedAccountsWithoutAccess(
  editorValue: AlinePlateValue,
  options: {
    mentionEntity: MentionEntity;
    isPrivate?: boolean;
  }
): {
  canEditMentionEntity: boolean;
  emailsWithoutAccess: string[];
  mentionedExternalUsers: MentionedUserSummary[];
  mentionEntityOwner: MentionEntityOwner | null;
} {
  const { isPrivate = false, mentionEntity } = options;
  const mentionEntityAccess = useMentionEntityAccess(mentionEntity);

  const mentionedEmails = useMemo(() => {
    if (editorValue.length === 0) return [];

    /**
     * The "@aline-ai" assistant mention isn't a user — exclude it so it's never treated as a
     * mentioned account lacking workflow access.
     */
    return PlateQueries.getMentions(editorValue)
      .map(([m]) => m.value)
      .filter(value => value !== ALINE_AI_MENTION_VALUE);
  }, [editorValue]);

  const mentionedExternalUsers = useDeepCompareMemo(
    () =>
      getMentionedExternalUsersForEmails({
        isPrivate,
        mentionedEmails,
        ...mentionEntityAccess,
      }),
    [isPrivate, mentionedEmails, mentionEntityAccess]
  );

  const emailsWithoutAccess = useDeepCompareMemo(
    () =>
      filterGrantAccessEmailsForPrivate(
        getEmailsWithoutAccess({
          documentPermissions: mentionEntityAccess.documentPermissions,
          mentionedEmails,
          storableOwnerEmail: mentionEntityAccess.storableOwnerEmail,
          workflowOwnerEmail: mentionEntityAccess.workflowOwnerEmail,
          workflowPermissions: mentionEntityAccess.workflowPermissions,
        }),
        isPrivate,
        mentionedExternalUsers
      ),
    [isPrivate, mentionedEmails, mentionedExternalUsers, mentionEntityAccess]
  );

  return {
    canEditMentionEntity: mentionEntityAccess.canEditMentionEntity,
    emailsWithoutAccess,
    mentionEntityOwner: mentionEntityAccess.mentionEntityOwner,
    mentionedExternalUsers,
  };
}

export function useMentionedAccountsWithoutAccessForUserIds(options: {
  mentionEntity: MentionEntity;
  isPrivate?: boolean;
}): {
  canEditMentionEntity: boolean;
  getEmailsWithoutAccessForUserIds: (
    mentionedUserIds: string[],
    directMentionEmails?: string[]
  ) => string[];
  getMentionedExternalUsersForUserIds: (
    mentionedUserIds: string[],
    directMentionEmails?: string[]
  ) => MentionedUserSummary[];
  mentionEntityOwner: MentionEntityOwner | null;
} {
  const { isPrivate = false, mentionEntity } = options;
  const mentionEntityAccess = useMentionEntityAccess(mentionEntity);

  const getMentionedEmailsForUserIds = useCallback(
    (mentionedUserIds: string[]) => {
      const mentionedUserIdSet = new Set(mentionedUserIds);
      return mentionEntityAccess.mentionableUsers.flatMap(user =>
        mentionedUserIdSet.has(user.id) ? [user.email] : []
      );
    },
    [mentionEntityAccess.mentionableUsers]
  );

  const getAllMentionedEmails = useCallback(
    (mentionedUserIds: string[], directMentionEmails: string[] = []) => {
      const fromUserIds = getMentionedEmailsForUserIds(mentionedUserIds);
      return [...new Set([...fromUserIds, ...directMentionEmails])];
    },
    [getMentionedEmailsForUserIds]
  );

  const getMentionedExternalUsersForUserIds = useCallback(
    (mentionedUserIds: string[], directMentionEmails: string[] = []) =>
      getMentionedExternalUsersForEmails({
        isPrivate,
        mentionedEmails: getAllMentionedEmails(mentionedUserIds, directMentionEmails),
        ...mentionEntityAccess,
      }),
    [getAllMentionedEmails, isPrivate, mentionEntityAccess]
  );

  const getEmailsWithoutAccessForUserIds = useCallback(
    (mentionedUserIds: string[], directMentionEmails: string[] = []) => {
      const mentionedEmails = getAllMentionedEmails(mentionedUserIds, directMentionEmails);
      const mentionedExternalUsers = getMentionedExternalUsersForEmails({
        isPrivate,
        mentionedEmails,
        ...mentionEntityAccess,
      });

      return filterGrantAccessEmailsForPrivate(
        getEmailsWithoutAccess({
          documentPermissions: mentionEntityAccess.documentPermissions,
          mentionedEmails,
          storableOwnerEmail: mentionEntityAccess.storableOwnerEmail,
          workflowOwnerEmail: mentionEntityAccess.workflowOwnerEmail,
          workflowPermissions: mentionEntityAccess.workflowPermissions,
        }),
        isPrivate,
        mentionedExternalUsers
      );
    },
    [getAllMentionedEmails, isPrivate, mentionEntityAccess]
  );

  return {
    canEditMentionEntity: mentionEntityAccess.canEditMentionEntity,
    getEmailsWithoutAccessForUserIds,
    getMentionedExternalUsersForUserIds,
    mentionEntityOwner: mentionEntityAccess.mentionEntityOwner,
  };
}

function useMentionEntityAccess(mentionEntity: MentionEntity) {
  const { permissions: documentPermissions } = useGetStorablePermissions(
    mentionEntity.__typename === 'Document' ? mentionEntity.id : null
  );
  const { mentionableUsers } = useGetMentionableUsers(mentionEntity, {
    permissions: documentPermissions,
  });
  const {
    canEditWorkflow,
    permissions: workflowPermissions,
    workflow,
  } = useCheckWorkflowAuthorization(
    mentionEntity.__typename === 'Workflow' ? mentionEntity.id : null
  );
  const { canEdit: canEditDocument, storable } = useIsAuthorizedToEditStorable(
    mentionEntity.__typename === 'Document' ? mentionEntity.id : null
  );

  return {
    canEditMentionEntity:
      mentionEntity.__typename === 'Document' ? canEditDocument : canEditWorkflow,
    documentPermissions,
    mentionEntityOwner:
      mentionEntity.__typename === 'Document'
        ? (storable?.owner ?? null)
        : (workflow?.owner ?? null),
    mentionableUsers,
    ownerOrgId:
      mentionEntity.__typename === 'Document' ? storable?.owner?.org?.id : workflow?.owner?.org?.id,
    storableOwner: storable?.owner ?? null,
    storableOwnerEmail: storable?.owner?.email ?? '',
    workflowOwner: workflow?.owner ?? null,
    workflowOwnerEmail: workflow?.owner?.email ?? '',
    workflowPermissions,
  };
}

function getMentionedExternalUsersForEmails({
  documentPermissions,
  isPrivate,
  mentionableUsers,
  mentionedEmails,
  ownerOrgId,
  storableOwner,
  workflowOwner,
  workflowPermissions,
}: ReturnType<typeof useMentionEntityAccess> & {
  isPrivate: boolean;
  mentionedEmails: string[];
}): MentionedUserSummary[] {
  if (!isPrivate || !ownerOrgId || !mentionedEmails.length) return [];

  const usersByEmail = new Map<
    string,
    { displayName: string; email: string; orgId?: string | null }
  >();
  function addUser(user: { displayName: string; email: string; org?: { id: string } | null }) {
    usersByEmail.set(user.email, {
      displayName: user.displayName,
      email: user.email,
      orgId: user.org?.id,
    });
  }

  for (const user of mentionableUsers) {
    addUser(user);
  }
  for (const permission of [...workflowPermissions, ...documentPermissions]) {
    if (permission.account?.__typename === 'User') addUser(permission.account);
  }
  if (storableOwner) addUser(storableOwner);
  if (workflowOwner) addUser(workflowOwner);

  return mentionedEmails.flatMap(email => {
    const user = usersByEmail.get(email);
    if (!user) return [{ displayName: email, email }];
    if (user.orgId === ownerOrgId) return [];

    return [{ displayName: user.displayName, email: user.email }];
  });
}

function filterGrantAccessEmailsForPrivate(
  withoutAccess: string[],
  isPrivate: boolean,
  mentionedExternalUsers: MentionedUserSummary[]
): string[] {
  if (!isPrivate) return withoutAccess;

  const externalEmails = new Set(mentionedExternalUsers.map(user => user.email));
  return withoutAccess.filter(email => !externalEmails.has(email));
}

function getEmailsWithoutAccess({
  documentPermissions,
  mentionedEmails,
  storableOwnerEmail,
  workflowOwnerEmail,
  workflowPermissions,
}: {
  documentPermissions: ReturnType<typeof useGetStorablePermissions>['permissions'];
  mentionedEmails: string[];
  storableOwnerEmail: string;
  workflowOwnerEmail: string;
  workflowPermissions: ReturnType<typeof useCheckWorkflowAuthorization>['permissions'];
}) {
  const permissionEmails = [...workflowPermissions, ...documentPermissions].flatMap(p => {
    return p.account?.__typename === 'User' ? [p.account.email] : [];
  });
  const allEmailsWithAccess = new Set(
    [...permissionEmails, storableOwnerEmail, workflowOwnerEmail].map(email => email.toLowerCase())
  );

  return mentionedEmails.filter(email => !allEmailsWithAccess.has(email.toLowerCase()));
}
